import { useCallback, useMemo } from "react";
import { useMyProducts } from "./useMyProducts";
import { useFullCurrentUser } from "./useCurrentUser";
import { useStores } from "./useStores";
import {
  isSelectable,
  productAvailability,
} from "../utils/product-availability";
import type { MyProduct } from "../types/products";

export interface UseProductFeaturesResult {
  /** The active product's plan codes for the current user type (or a debug override). */
  productPlans: string[];
  /** The product currently active for this user, if resolvable. */
  activeProduct: MyProduct | undefined;
  matchesProductPlan: (plan: string) => boolean;
  matchesProductPlans: (plans: string[] | null | undefined) => boolean;
  proskillPlans: string[];
  msPlans: string[];
  wblPlans: string[];
  isLoading: boolean;
  isDebugOverride: boolean;
  setDebugProductPlan: (newPlan: string[] | null) => void;
  refetch: () => void;
}

/** Developer override stored in localStorage; when set it replaces productPlans. */
const getDebugPlans = (): string[] | null => {
  try {
    const stored = localStorage.getItem("debug_product_plan");
    return stored ? (JSON.parse(stored) as string[]) : null;
  } catch (e) {
    console.error("Invalid debug_product_plan in localStorage", e);
    return null;
  }
};

/**
 * Returns the product-plan codes for the user's ACTIVE product, scoped to the
 * active user type: climbers (`cli-`) get the product's `learnerPlanCodes`,
 * everyone else gets `educatorPlanCodes`. The active product is the one the
 * user selected post-login (persisted as `activeProductCode`), or the sole
 * product when they only have one.
 *
 * Example:
 *   const { matchesProductPlan } = useProductFeatures();
 *   if (matchesProductPlan("ms-staff")) { ... }
 */
export const useProductFeatures = (): UseProductFeaturesResult => {
  const { products, loading, refetch } = useMyProducts();
  const { activeUserRecord } = useFullCurrentUser();
  const { userStore } = useStores();

  const activeProductCode: string | null = userStore?.getActiveProductCode();

  const isClimber = activeUserRecord?.role === "climber";

  const activeProduct = useMemo<MyProduct | undefined>(() => {
    // A climber cannot be active in a program outside its schedule, whether it
    // was chosen before the program closed or is simply the only one they hold.
    // Resolving one here would reopen the entry gate from the side: post-login
    // refuses it, but a bookmark or a second tab never passes through
    // post-login, and the sole-product fallback below used to resolve a closed
    // program unconditionally. With it refused, protectedRoute's existing
    // "products but none active" guard sends them to the selector instead.
    //
    // Educators are exempt for the same reason they are in post-login: they
    // have no selector to be sent to, and their plan codes come from every
    // product they hold rather than from this one.
    const usable = (product: MyProduct) =>
      !isClimber || isSelectable(productAvailability(product));

    if (activeProductCode) {
      const match = products.find((p) => p.code === activeProductCode);
      if (match && usable(match)) return match;
    }
    // Fall back to the sole product when there's exactly one.
    return products.length === 1 && usable(products[0])
      ? products[0]
      : undefined;
  }, [products, activeProductCode, isClimber]);

  const debugProductPlans = getDebugPlans();

  const productPlans = useMemo<string[]>(() => {
    if (debugProductPlans) return debugProductPlans;
    if (isClimber) {
      if (!activeProduct) return [];
      return activeProduct.learnerPlanCodes;
    }
    // Educators get the union of plan codes across all their products so that
    // features from every product they belong to are available simultaneously.
    return [...new Set(products.flatMap((p) => p.educatorPlanCodes))];
  }, [debugProductPlans, activeProduct, isClimber, products]);

  // console.info("Active product:", activeProduct?.code, productPlans);
  const proskillPlans = useMemo(
    () => productPlans.filter((plan) => plan.startsWith("proskills")),
    [productPlans],
  );
  const wblPlans = useMemo(
    () => productPlans.filter((plan) => plan.startsWith("wbl")),
    [productPlans],
  );
  const msPlans = useMemo(
    () => productPlans.filter((plan) => plan.startsWith("ms-")),
    [productPlans],
  );

  // For internal developer use only.
  const setDebugProductPlan = useCallback((newPlan: string[] | null) => {
    if (Array.isArray(newPlan)) {
      localStorage.setItem("debug_product_plan", JSON.stringify(newPlan));
    } else {
      localStorage.removeItem("debug_product_plan");
    }
  }, []);

  const matchesProductPlan = useCallback(
    (plan: string) => productPlans.includes(plan),
    [productPlans],
  );

  const matchesProductPlans = useCallback(
    (plans: string[] | null | undefined) => {
      if (!plans) return false;
      return productPlans.some((plan) => plans.includes(plan));
    },
    [productPlans],
  );

  return {
    productPlans,
    activeProduct,
    matchesProductPlan,
    matchesProductPlans,
    proskillPlans,
    msPlans, // TODO - remove this
    wblPlans, // TODO - remove this
    isLoading: loading,
    isDebugOverride: debugProductPlans !== null,
    setDebugProductPlan,
    refetch: () => {
      refetch();
    },
  };
};
